import React, { useEffect, useState } from "react";
import {
  Button,
  Flex,
  Loader,
  Radio,
  RadioGroupField,
  TextField,
  Text,
  PasswordField,
  useAuthenticator,
  Input,
  Icon,
  TextAreaField,
  SelectField,
  Alert,
  Autocomplete,
  Badge,
  View
} from "@aws-amplify/ui-react";
import "./Task.css";
import { createTaskAPI, getUsersListAPI } from "../../data/api";
import DatePicker from 'react-datepicker'
import it from "date-fns/locale/it";
import 'react-datepicker/dist/react-datepicker.min.css'
import { format } from "date-fns";
import { MdCancel, MdDelete, MdEditCalendar } from "react-icons/md";
import { useLocation } from "react-router-dom";
import { I18n } from "aws-amplify/utils";
import emitter from '../../eventBus/EventBus';

const defaultForm = {
  operatori: [],
  dataTask: new Date(),
  link: '',
  testo: '',
  tipologia: 'Singolo'
};

const TaskModal = (props) => {
  const { user } = useAuthenticator((context) => [context.user]);
  defaultForm.operatori = [user?.username]
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [errorCreate, setErrorCreate] = useState(false);
  const [searchOperatoreValue, setSearchOperatoreValue] = useState('');
  const location = useLocation();

  // create user form data
  const [createTaskFormValues, setCreateTaskFormValues] = useState(defaultForm);

  const changeInputCreateTaskForm = (e) => {
    const { name, value } = e.target;
    const newValue = {
      ...createTaskFormValues,
      [name]: value,
    };
    setCreateTaskFormValues(newValue);
  };

  const resetCreateTaskForm = () => {
    setCreateTaskFormValues(defaultForm);
  }

  const createTask = async () => {
    try{
      setLoadingCreate(true);
      setErrorCreate(false);
      await createTaskAPI(createTaskFormValues.operatori, createTaskFormValues.dataTask, createTaskFormValues.link, createTaskFormValues.testo, createTaskFormValues.tipologia);
      setLoadingCreate(false);
      resetCreateTaskForm();
      props.onCreate();
      emitter.emit('createdNewTask');
    } catch (e) {
      console.log('Create task failed ', e);
      setErrorCreate(true);
      setLoadingCreate(false);
    }
  };

  // load operatori list
  const [operatori, setOperatori] = useState([]);
  const [loadingOperatori, setLoadingOperatori] = useState(true);
  const [errorOperatori, setErrorOperatori] = useState(false);
  const getOperatoriList = async () => {
    setLoadingOperatori(true)
    setErrorOperatori(false);
    try {
      const usersList = await getUsersListAPI();
      setOperatori(usersList)
      setLoadingOperatori(false);
    } catch (e) {
      console.log('Could not load users list: ', e);
      setErrorOperatori(true);
      setLoadingOperatori(false);
    }
  };

  useEffect(() => {
    setCreateTaskFormValues({
      ...createTaskFormValues,
      link: location.pathname + location.search + location.hash
    })
  }, [location])
  
  useEffect(() => {
    getOperatoriList();
  }, [])

  useEffect(() => {
    if (createTaskFormValues.operatori.length < 2) {
      setCreateTaskFormValues((prevValues) => ({
        ...prevValues,
        tipologia: 'Singolo'
      }));
    }
  }, [createTaskFormValues.operatori]);

  useEffect(() => {
    if (createTaskFormValues.dataTask === null) {
      setCreateTaskFormValues((prevValues) => ({
        ...prevValues,
        dataTask: new Date()
      }));
    }
  }, [createTaskFormValues.dataTask]);

  const removeOperatore = (operatore) => {
    changeInputCreateTaskForm({
        target: {
          name: 'operatori', 
          value: createTaskFormValues.operatori.filter(o => o !== operatore)
        }
      }
    )
  }

  return (
    <Flex as="form" direction="column" width="100%" onSubmit={(e) => {e.preventDefault(); createTask();}}>
      <TextAreaField
        value={createTaskFormValues.testo}
        onChange={changeInputCreateTaskForm}
        name="testo"
        label={
          <Text>
            <b>Testo</b>
          </Text>
        }
        isRequired={true}>
      </TextAreaField>
      <Text fontWeight="bold">Operatori assegnatari</Text>
      {createTaskFormValues.operatori.length > 0 && 
      <View>
        {
          createTaskFormValues.operatori.map(o => 
            <Badge variation="info" width="fit-content" marginRight="10px" marginBottom="10px">
              <Text>{o}</Text>
              {
                createTaskFormValues.operatori.length > 1 &&
                <Icon marginLeft="5px" as={MdCancel} onClick={() => removeOperatore(o)}></Icon>
              }
            </Badge>
          )
        }
      </View>}
      {
        errorOperatori ? <Alert variation="error">Errore nel caricamento della lista utenti</Alert> : 
        <Autocomplete
          label={
            <Text>
              <b>Assegnatari</b>
            </Text>
          }
          isLoading={loadingOperatori}
          value={searchOperatoreValue}
          placeholder="Cerca utente..."
          options={operatori.map(o => {return {id: o.username, label: o.username}}).filter(o => !createTaskFormValues.operatori.includes(o.id))}
          onSelect={(value) => {changeInputCreateTaskForm({target: {name: 'operatori', value: [...createTaskFormValues.operatori, value.id]}}); setSearchOperatoreValue('')}}
          onChange={(e) => setSearchOperatoreValue(e.target.value)}
          onClear={() => setSearchOperatoreValue('')}
          menuSlots={{
            LoadingIndicator: (
              <Flex alignItems="center" gap="0.25rem">
                <Loader />
                Caricamento utenti...
              </Flex>
            ),
            Empty: (<View><i>Nessun utente trovato</i></View>)
          }}
        />
      }
      <RadioGroupField
        legend={
          <Text>
            <b>Tipologia</b>
          </Text>
        }
        name="tipologia"
        value={createTaskFormValues.tipologia}
        onChange={changeInputCreateTaskForm}
        isRequired={true}
        isReadOnly={createTaskFormValues.operatori.length<2}
      >
        <Radio value='Singolo'>Task singolo</Radio>
        <Radio value='Gruppo'>Task di gruppo</Radio>
      </RadioGroupField>
      <DatePicker
        onChange={(date) => changeInputCreateTaskForm({target: {name: 'dataTask', value: date}})}
        selected={createTaskFormValues.dataTask ?? (new Date())}
        locale={it}
        dateFormat="dd/MM/yyyy"
        customInput= {
          <TextField
            name="name"
            readOnly
            isRequired={true}
            label={
              <Text>
                <b>Data del task</b>
              </Text>
            }>
          </TextField>
        }
      />
      <Button
        type="submit"
        variation="primary"
        width={{ base: "100%", large: "100%" }}
        disabled={loadingCreate}
      >
        {loadingCreate ? <Loader/> : "Crea task"}
      </Button>
      { errorCreate ? <span style={{color: "red", marginLeft: "auto"}}>Errore nella creazione del task!</span> : ""}
    </Flex>
  );
};

export default TaskModal;
